import { Helmet } from 'react-helmet-async';
import { studioUrl } from '@/routing/studioHost';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  Activity,
  BadgeCheck,
  FileSearch,
  Flag,
  Lightbulb,
  FolderKanban,
  HeartPulse,
  ListVideo,
  Mail,
  MessageSquareWarning,
  MapPinned,
  Mic,
  Menu,
  PackageSearch,
  Radio,
  ShieldAlert,
  Sparkles,
  Tags,
  UsersRound,
  X,
  Download,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import SkipLink from '@/components/layout/SkipLink';
import { Logo } from '@/components/ui/Logo';
import { useAccountTheme } from '@/components/layout/useAccountTheme';

import './accountLayout.css';
import './adminLayout.css';

export type AdminLayoutProps = {
  variant?: 'default' | 'operations' | 'taxonomy' | 'user-form';
};

type AdminConsoleNavigationItem = {
  label: string;
  to: string;
  icon: typeof Activity;
  activePaths?: readonly string[];
  disabled?: true;
  badge?: string;
  /** Lives on the studio origin, so it renders as a full-page link. */
  studio?: true;
};

type AdminNavigationGroup = { label: string; items: readonly AdminConsoleNavigationItem[] };

// One grouped list on every admin page, so the sidebar does not change as you move.
export const ADMIN_NAVIGATION_GROUPS: readonly AdminNavigationGroup[] = [
  {
    label: 'Insights',
    items: [
      { label: 'Pulse', to: '/admin', icon: Activity },
      { label: 'Emails', to: '/admin/emails', icon: Mail, disabled: true, badge: 'Soon' },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { label: 'Sequence editor', to: '/admin/sequences', icon: ListVideo },
      { label: 'Displays', to: '/admin/displays', icon: MapPinned },
      { label: 'Categories', to: '/admin/taxonomy/categories', icon: Tags },
      { label: 'Prop catalog', to: '/admin/prop-catalog', icon: PackageSearch },
    ],
  },
  {
    label: 'Review queues',
    items: [
      { label: 'Claims', to: '/admin/claims', icon: BadgeCheck },
      { label: 'Reports', to: '/admin/reports', icon: Flag },
      { label: 'Moderation', to: '/admin/moderation', icon: ShieldAlert },
      { label: 'Feedback reports', to: '/admin/feedback-reports', icon: MessageSquareWarning },
      { label: 'Ideas board', to: '/admin/feedback-ideas', icon: Lightbulb },
      { label: 'Enrichment', to: '/admin/enrichment', icon: Sparkles },
      { label: 'Voice rewards', to: '/admin/voice-rewards', icon: Mic },
      { label: 'Metadata review', to: '/admin/metadata-review', icon: FileSearch },
    ],
  },
  {
    label: 'People',
    items: [
      { label: 'Users', to: '/admin/users', icon: UsersRound },
      { label: 'Download limits', to: '/admin/download-limits', icon: Download },
      { label: 'Directory and outreach', to: '/admin/directory', icon: FolderKanban },
    ],
  },
  {
    label: 'Ops',
    items: [
        { label: 'Live relays', to: '/admin/relays', icon: Radio },
      { label: 'Download health', to: '/admin/download-health', icon: HeartPulse },
      { label: 'Lighting studio', to: '/projects', icon: Lightbulb, studio: true, badge: 'Not launched' },
    ],
  },
];

type AdminConsoleLayoutProps = {
  note: string;
  mainAriaLabel?: string;
};

const AdminConsoleLayout = ({ note, mainAriaLabel = 'Admin workspace' }: AdminConsoleLayoutProps) => {
  useAccountTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mobileMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const navigationRef = useRef<HTMLElement | null>(null);
  const location = useLocation();

  useEffect(() => {
    document.body.classList.add('admin-theme');
    return () => document.body.classList.remove('admin-theme');
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return;

    navigationRef.current?.querySelector<HTMLAnchorElement>('a[href]')?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        const links = Array.from(navigationRef.current?.querySelectorAll<HTMLAnchorElement>('a[href]') ?? []);
        const controls = [mobileMenuButtonRef.current, ...links].filter((control): control is HTMLButtonElement | HTMLAnchorElement => Boolean(control));
        const index = controls.indexOf(document.activeElement as HTMLAnchorElement);
        if (event.shiftKey && index <= 0) {
          event.preventDefault();
          controls.at(-1)?.focus();
        } else if (!event.shiftKey && (index < 0 || index === controls.length - 1)) {
          event.preventDefault();
          controls[0]?.focus();
        }
        return;
      }
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setMobileNavOpen(false);
      mobileMenuButtonRef.current?.focus();
    };
    const handleResize = () => {
      if (window.innerWidth > 860) setMobileNavOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, [mobileNavOpen]);

  return (
    <div className="admin-console-shell account-theme">
      <SkipLink />
      <Helmet>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <header className="admin-console-header">
        <button
          type="button"
          ref={mobileMenuButtonRef}
          className="admin-console-header__menu"
          aria-controls="admin-console-navigation"
          aria-expanded={mobileNavOpen}
          aria-label={mobileNavOpen ? 'Close admin navigation' : 'Open admin navigation'}
          onClick={() => setMobileNavOpen((open) => !open)}
        >
          {mobileNavOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
        <Link to="/" className="admin-console-header__brand" aria-label="Return to Light Hunt">
          <Logo />
        </Link>
        <span className="admin-console-header__label">Admin</span>
        <Link to="/my-downloads" className="admin-console-header__exit">Exit admin</Link>
      </header>
      <div className="admin-console-frame">
        <aside
          ref={navigationRef}
          id="admin-console-navigation"
          className="admin-console-sidebar"
          aria-label="Admin console navigation"
          data-mobile-open={mobileNavOpen ? 'true' : 'false'}
        >
          <nav aria-label="Admin sections">
            {ADMIN_NAVIGATION_GROUPS.map((group) => {
              const groupId = `admin-nav-group-${group.label.toLowerCase().replace(/[^a-z]+/g, '-')}`;
              return (
                <div key={group.label} role="group" aria-labelledby={groupId} className="admin-console-nav-group">
                  <p id={groupId} className="admin-console-nav-group__label">{group.label}</p>
                  {group.items.map(({ label, to, icon: Icon, activePaths, disabled, badge, studio }) => {
                    if (studio) {
                      return (
                        <a key={to} href={studioUrl(to)} className="admin-console-nav-link">
                          <Icon className="admin-console-nav-link__icon" aria-hidden="true" />
                          <span className="admin-console-nav-link__label">{label}</span>
                          {badge && <span className="admin-console-nav-link__badge">{badge}</span>}
                        </a>
                      );
                    }
                    if (disabled) {
                      return (
                        <span key={to} aria-disabled="true" className="admin-console-nav-link admin-console-nav-link--disabled">
                          <Icon className="admin-console-nav-link__icon" aria-hidden="true" />
                          <span className="admin-console-nav-link__label">{label}</span>
                          {badge && <span className="admin-console-nav-link__badge">{badge}</span>}
                        </span>
                      );
                    }
                    const isPathActive = (path: string) =>
                      location.pathname === path ||
                      (path !== '/admin' && location.pathname.startsWith(`${path}/`));
                    const isActive = (activePaths ?? [to]).some(isPathActive);
                    return (
                      <Link
                        key={to}
                        to={to}
                        aria-current={isActive ? 'page' : undefined}
                        onClick={() => setMobileNavOpen(false)}
                        className={`admin-console-nav-link${isActive ? ' admin-console-nav-link--active' : ''}`}
                      >
                        <Icon className="admin-console-nav-link__icon" aria-hidden="true" />
                        <span className="admin-console-nav-link__label">{label}</span>
                      </Link>
                    );
                  })}
                </div>
              );
            })}
          </nav>
          <p className="admin-console-sidebar__note">{note}</p>
        </aside>
        {mobileNavOpen && (
          <button
            type="button"
            className="admin-console-backdrop"
            aria-label="Close admin navigation"
            onClick={() => setMobileNavOpen(false)}
          />
        )}
        <main
          id="main-content"
          className="admin-console-main"
          aria-label={mainAriaLabel}
          tabIndex={-1}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const TaxonomyAdminLayout = () => (
  <AdminConsoleLayout
    note="Review category changes before saving."
    mainAriaLabel="Admin taxonomy workspace"
  />
);

const UserFormAdminLayout = () => (
  <AdminConsoleLayout
    note="Confirm the selected account before saving changes."
    mainAriaLabel="Admin user form"
  />
);

const AdminLayout = ({ variant = 'default' }: AdminLayoutProps) => {
  if (variant === 'operations' || variant === 'default') {
    return (
      <AdminConsoleLayout
        note="Changes affect live listings and accounts. Review each action before confirming."
      />
    );
  }
  if (variant === 'taxonomy') return <TaxonomyAdminLayout />;
  if (variant === 'user-form') return <UserFormAdminLayout />;

  return <Outlet />;
};

export default AdminLayout;
